import React, { useEffect, useState } from "react";

import "./AssessmentHistory.css";
import "../../Components/Commons/Commons.css";
import "quill/dist/quill.snow.css";
import "chart.js/auto";

import AntDTable from "../AntDTable/AntDTable";
import BtnSave from "../Commons/Buttons/BtnSave";
import ViewAssessment from "./ViewAssessment";
import ViewPrescription from "./ViewPrescription";
import BirForm from "./PrintEkas";
import PrintEpress from "./PrintEpress";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Button, Card, Tag } from "antd";
import { Col, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getAssessmentHistory,
  getAssessmentForPrint,
} from "../Helpers/API/AssessmentApi/AssessmentApi";
import { formatDate } from "../../Assets/Helpers/Common";
import { RefreshRounded } from "@mui/icons-material";
import {
  capitalizeText,
  formatDateWithTime,
  formatDateYYYYMMDD,
  refreshPage,
} from "../Helpers/Utilities/Common";
import BtnClearDropdown from "../Commons/Buttons/BtnClearDropdown";
import PrintLaboratoryDiagnostics from "../Diagnosis/PrintLaboratoryDiagnostics";
import PrintXrayDiagnostics from "../Diagnosis/PrintXrayDiagnostics";
import PrintCTScanDiagnostics from "../Diagnosis/PrintCTScanDiagnostics";
import PrintUltrasoundDiagnostics from "../Diagnosis/PrintUltrasoundDiagnostics";
import PrintAdmissionForm from "../Forms/AdmissionForm/PrintAdmissionForm";

// Function for medication format of medicine
function MedicationInfo({ therapeutics }) {
  return (
    <ul className="m-0 p-0">
      {therapeutics?.map((med) => {
        return (
          <li className="m-0 p-0">
            <div>{med?.quantity}</div>
            <div>{med?.dosage}</div>
            <div>{med?.form}</div>
            <div>{med?.generic}</div>
            <div>{med?.brand}</div>
            <div>{med?.duration}</div>
            <div>{med?.preparation}</div>
          </li>
        );
      })}
    </ul>
  );
}

//Function for status data to change color in table
function StatusTag({ status }) {
  if (status === "ongoing") {
    return <Tag color="red">Ongoing</Tag>;
  } else if (status === "ended") {
    return <Tag color="green">Ended</Tag>;
  } else if (status === "for_labs") {
    return <Tag color="yellow">For Labs</Tag>;
  } else if (status === "pending_payment") {
    return <Tag color="orange">Pending Payment</Tag>;
  } else {
    return <Tag>{status}</Tag>;
  }
}

//Function for type data to change color in table
function TypeTag({ type }) {
  if (type === "admit") {
    return <Tag color="cyan">Admit</Tag>;
  } else if (type === "outpatient") {
    return <Tag color="blue">Outpatient</Tag>;
  } else if (type === "for_admission") {
    return <Tag color="orange">For Admission</Tag>;
  } else {
    return <Tag>{type}</Tag>;
  }
}

function AssessmentHistory({ patientId }) {
  const [data, setData] = useState([]);
  const [showViewAssessmentModal, setShowViewAssessmentModal] = useState(false);
  const [showViewPrescriptionModal, setShowViewPrescriptionModal] =
    useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [isNew, setIsNew] = useState("");
  const [showPrintLabDiagnosticsModal, setShowPrintLabDiagnosticsModal] =
    useState(false);
  const [showPrintXrayDiagnosticsModal, setShowPrintXrayDiagnosticsModal] =
    useState(false);
  const [showPrintCTScanDiagnosticsModal, setShowPrintCTScanDiagnosticsModal] =
    useState(false);
  const [
    showPrintUltrasoundDiagnosticsModal,
    setShowPrintUltrasoundDiagnosticsModal,
  ] = useState(false);
  const [showPrintAdmissionModal, setShowPrintAdmissionModal] = useState(false);
  const [selectedPatientDetails, setSelectedPatientDetails] = useState([]);
  const [showPrintEkas, setShowPrintEkas] = useState(false);
  const [showPrintEpress, setShowPrintEpress] = useState(false);

  //Function for Action button will be added in the table
  function ActionBtn(row) {
    const diagnosticItems = row?.row?.diagnostics?.[0]?.diagnostic_items || [];
    // Only include items where is_new === 1
    const filteredNewItems = diagnosticItems.filter(
      (item) => item?.is_new === "1"
    );
    // Extract available types
    const availableTypes = filteredNewItems.reduce((acc, item) => {
      const type = item?.type?.toLowerCase();
      const sub_type = item?.sub_type?.toLowerCase();

      if (type === "laboratory" && !acc.includes("laboratory"))
        acc.push("laboratory");
      if (type === "radiology") {
        if (
          ["xray", "ct_scan", "ultrasound"].includes(sub_type) &&
          !acc.includes(sub_type)
        ) {
          acc.push(sub_type);
        }
      }

      return acc;
    }, []);

    const menu = [
      { key: "laboratory", label: "Laboratory" },
      { key: "xray", label: "X-Ray" },
      { key: "ct_scan", label: "CT Scan" },
      { key: "ultrasound", label: "Ultrasound" },
    ].filter((item) => availableTypes.includes(item.key));

    // Only include items where is_new === 0
    const filteredOldItems = diagnosticItems.filter(
      (item) => item?.is_new === "0"
    );

    const availableOldTypes = filteredOldItems.reduce((acc, item) => {
      const type = item?.type?.toLowerCase();
      const sub_type = item?.sub_type?.toLowerCase();

      if (type === "laboratory" && !acc.includes("laboratory"))
        acc.push("laboratory");
      if (type === "radiology") {
        if (
          ["xray", "ct_scan", "ultrasound"].includes(sub_type) &&
          !acc.includes(sub_type)
        ) {
          acc.push(sub_type);
        }
      }

      return acc;
    }, []);

    const oldMenu = [
      { key: "laboratory", label: "Laboratory" },
      { key: "xray", label: "X-Ray" },
      { key: "ct_scan", label: "CT Scan" },
      { key: "ultrasound", label: "Ultrasound" },
    ].filter((item) => availableOldTypes.includes(item.key));

    async function handlePrintEkas() {
      setShowLoader(true);
      const response = await getAssessmentForPrint({
        assessment_id: row?.row?.id,
        patient_id: patientId,
      });
      if (response?.data) {
        setSelectedPatientDetails(response.data.data);
        setShowPrintEkas(true);
        await handleGenerateEkasForm();
      } else if (response?.error) {
        response.error.data.messages.error_messages.forEach((err) =>
          toast.error(err || "Failed to fetch data.")
        );
      }
      setShowLoader(false);
    }

    async function handlePrintEpress() {
      setShowLoader(true);
      const response = await getAssessmentForPrint({
        assessment_id: row?.row?.id,
        patient_id: patientId,
      });
      if (response?.data) {
        setSelectedPatientDetails(response.data.data);
        setShowPrintEpress(true);
        await handleGenerateEpressForm();
      } else if (response?.error) {
        response.error.data.messages.error_messages.forEach((err) =>
          toast.error(err || "Failed to fetch data.")
        );
      }
      setShowLoader(false);
    }

    return (
      <>
        <Row className="center px-1">
          <Col className="m-0 p-0">
            <BtnSave
              id={"view-btn"}
              label={"View"}
              size={"sm"}
              onClick={(e) => {
                setSelectedRow(row.row);
                setShowViewAssessmentModal(true);
              }}
            />
          </Col>
          <Col className="m-0 p-0">
            <BtnSave
              id={"printRx-button"}
              label={"View Rx"}
              size={"sm"}
              onClick={(e) => {
                setSelectedRow(row.row);
                setShowViewPrescriptionModal(true);
              }}
              disabled={row?.row?.therapeutics?.length === 0}
            />
          </Col>
        </Row>
        <Row className="center px-2">
          <Col className="m-0 p-0">
            <BtnSave
              id={"printRx-button"}
              label={"Print eKas"}
              size={"sm"}
              onClick={(e) => {
                setSelectedRow(row.row);
                handlePrintEkas();
              }}
              isClicked={showLoader}
              disabled={!(row?.row?.formatted_status === "Ended")}
            />
          </Col>
          <Col className="m-0 p-0">
            <BtnSave
              id={"view-btn"}
              label={"Print ePresS"}
              size={"sm"}
              onClick={(e) => {
                setSelectedRow(row.row);
                handlePrintEpress();
              }}
              isClicked={showLoader}
              disabled={!(row?.row?.formatted_status === "Ended")}
            />
          </Col>
        </Row>
        {(row?.row?.formatted_type?.toLowerCase() === "for_admission" ||
          row?.row?.formatted_type?.toLowerCase() === "admit") && (
          <>
            <Row className="center px-2">
              <Col className="m-0 p-0">
                <BtnSave
                  id={"view-btn"}
                  label={"View Admission Form"}
                  size={"sm"}
                  onClick={(e) => {
                    setSelectedRow(row.row);
                    setShowPrintAdmissionModal(true);
                  }}
                  disabled={row?.row?.inpatients?.length === 0}
                />
              </Col>
            </Row>
          </>
        )}
        <Row className="center">
          <Col>
            <BtnClearDropdown
              label={"Prev. Requests"}
              size={"sm"}
              menuItems={oldMenu}
              onSelect={(key) => {
                if (key !== "none") {
                  var _diagnostics = row?.row?.diagnostics[0];
                  var printDiagnostics = {
                    laboratory: [],
                    xray: [],
                    ct_scan: [],
                    ultrasound: [],
                    others: [],
                  };
                  _diagnostics.diagnostic_items &&
                    _diagnostics.diagnostic_items.forEach((lab) => {
                      var type = lab?.type?.toLowerCase();
                      var sub_type = lab?.sub_type?.toLowerCase();

                      var item = {
                        lab_test_name: lab?.lab_name || "",
                        lab_schedule: lab?.lab_schedule || "",
                        is_new: lab?.is_new || "",
                      };

                      if (type === "laboratory") {
                        printDiagnostics.laboratory.push(item);
                      } else if (type === "radiology" || type === "imaging") {
                        if (sub_type === "xray") {
                          printDiagnostics.xray.push(item);
                        } else if (sub_type === "ct_scan") {
                          printDiagnostics.ct_scan.push(item);
                        } else if (sub_type === "ultrasound") {
                          printDiagnostics.ultrasound.push(item);
                        } else {
                          printDiagnostics.others.push(item);
                        }
                      } else {
                        printDiagnostics.others.push(item);
                      }
                    });
                  setIsNew("0");
                  setSelectedRow({
                    ...row.row,
                    printDiagnostics,
                  });

                  handlePrintDiagnostics(key);
                }
              }}
              disabled={
                !row?.row?.diagnostics?.length ||
                !row.row.diagnostics[0]?.diagnostic_items?.length ||
                !oldMenu?.length
              }
            />
          </Col>
          <Col>
            <BtnClearDropdown
              label={"View New Requests"}
              size={"sm"}
              menuItems={menu}
              onSelect={(key) => {
                if (key !== "none") {
                  var _diagnostics = row?.row?.diagnostics[0];
                  var printDiagnostics = {
                    laboratory: [],
                    xray: [],
                    ct_scan: [],
                    ultrasound: [],
                    others: [],
                  };
                  _diagnostics.diagnostic_items &&
                    _diagnostics.diagnostic_items.forEach((lab) => {
                      var type = lab?.type?.toLowerCase();
                      var sub_type = lab?.sub_type?.toLowerCase();

                      var item = {
                        lab_test_name: lab?.lab_name || "",
                        lab_schedule: lab?.lab_schedule || "",
                        is_new: lab?.is_new || "",
                      };

                      if (type === "laboratory") {
                        printDiagnostics.laboratory.push(item);
                      } else if (type === "radiology" || type === "imaging") {
                        if (sub_type === "xray") {
                          printDiagnostics.xray.push(item);
                        } else if (sub_type === "ct_scan") {
                          printDiagnostics.ct_scan.push(item);
                        } else if (sub_type === "ultrasound") {
                          printDiagnostics.ultrasound.push(item);
                        } else {
                          printDiagnostics.others.push(item);
                        }
                      } else {
                        printDiagnostics.others.push(item);
                      }
                    });
                  setIsNew("1");
                  setSelectedRow({
                    ...row.row,
                    printDiagnostics,
                  });
                  handlePrintDiagnostics(key);
                }
              }}
              disabled={
                !row?.row?.diagnostics?.length ||
                !row.row.diagnostics[0]?.diagnostic_items?.length ||
                !menu?.length
              }
            />
          </Col>
        </Row>
      </>
    );
  }

  // Function for showing which diagnostic to print
  function handlePrintDiagnostics(menu) {
    if (menu === "laboratory") {
      setShowPrintLabDiagnosticsModal(true);
    } else if (menu === "xray") {
      setShowPrintXrayDiagnosticsModal(true);
    } else if (menu === "ct_scan") {
      setShowPrintCTScanDiagnosticsModal(true);
    } else if (menu === "ultrasound") {
      setShowPrintUltrasoundDiagnosticsModal(true);
    }
  }

  async function handleGenerateEkasForm() {
    toast.loading("Printing Ekas Form");
    setTimeout(() => {
      toast.dismiss();
      waitForElementAndPrint("printablediv", executeEkasPrint);
    }, 1500);
  }

  async function handleGenerateEpressForm() {
    toast.loading("Printing Epress Form");
    setTimeout(() => {
      toast.dismiss();
      waitForElementAndPrint("printablediv2", executeEpressPrint);
    }, 1500);
  }

  function waitForElementAndPrint(elementId, printFunction) {
    const checkElement = () => {
      const element = document.getElementById(elementId);
      if (element && element.innerHTML && element.innerHTML.trim().length > 100) {
        printFunction();
      } else {
        setTimeout(checkElement, 200);
      }
    };
    checkElement();
  }

  function executeEkasPrint() {
    const printElement = document.getElementById("printablediv");
    if (!printElement || !printElement.innerHTML) {
      toast.error("Print content not ready. Please try again.");
      setShowPrintEkas(false);
      return;
    }

    let printContents = printElement.innerHTML;
    let originalContents = document.body.innerHTML;
    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
    setShowPrintEkas(false);
    refreshPage();
  }

  function executeEpressPrint() {
    const printElement = document.getElementById("printablediv2");
    if (!printElement || !printElement.innerHTML) {
      toast.error("Print content not ready. Please try again.");
      setShowPrintEpress(false);
      return;
    }

    let printContents = printElement.innerHTML;
    let originalContents = document.body.innerHTML;
    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
    setShowPrintEpress(false);
    refreshPage();
  }

  //Function get Assessment History API
  // async function _fetchAssessmentHistory() {
  //   setShowLoader(true)
  //   const res = await getAssessmentHistory({ patient_id: patientId })

  //   if (res.data) {
  //     var _parsed = res.data.data.map((assessment) => {
  //       return {
  //         ...assessment,
  //         medication: <MedicationInfo therapeutics={assessment.therapeutics} />,
  //         status: <StatusTag status={assessment.status} />,
  //         type: <TypeTag type={assessment.type} />,
  //         formatted_status: capitalizeText(assessment.status),
  //         formatted_type: capitalizeText(assessment.type),
  //         assessment_date: assessment.is_sunday_patient === "1" ?
  //           formatDate(assessment.last_checkup_date) : formatDate(assessment.added_on),
  //         fomattedAddedOn: formatDateWithTime(assessment.added_on)
  //         }

  //     })
  //     setData(_parsed)
  //   } else if (res.error) {
  //     setData([])
  //   }
  //   setShowLoader(false)
  // }

  async function _fetchAssessmentHistory() {
    setShowLoader(true);
    const res = await getAssessmentHistory({ patient_id: patientId });

    if (res.data) {
      var _parsed = res.data.data.map((assessment) => {
        // Access only the first diagnostic if available
        var firstDiagnostic = assessment.diagnostics?.[0];

        // Format the diagnostic_items from the first diagnostic only
        var diagnosticItems =
          firstDiagnostic?.diagnostic_items?.map((item) => ({
            ...item,
            formatted_lab_type:
              item.type === "laboratory" ? "laboratory" : item.sub_type,
          })) || [];

        // Add diagnosticItems to the first diagnostic if it exists
        var updatedDiagnostics = firstDiagnostic
          ? [
              {
                ...firstDiagnostic,
                diagnostic_items: diagnosticItems,
              },
            ]
          : [];

        // Format diagnosis as bullet list
        const formattedDiagnosis = (
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            {(assessment.diagnosis || []).map((d, idx) => (
              <li key={idx}>{d.diagnosis_name}</li>
            ))}
          </ul>
        );

        return {
          ...assessment,
          medication: <MedicationInfo therapeutics={assessment.therapeutics} />,
          status: <StatusTag status={assessment.status} />,
          type: <TypeTag type={assessment.type} />,
          formatted_status: capitalizeText(assessment.status),
          formatted_type: capitalizeText(assessment.type),
          assessment_date:
            assessment.is_sunday_patient === "1"
              ? formatDate(assessment.last_checkup_date)
              : formatDate(assessment.added_on),
          raw_assessment_date:
            assessment.is_sunday_patient === "1"
              ? assessment.last_checkup_date
              : assessment.added_on,
          fomattedAddedOn: formatDateWithTime(assessment.added_on),
          diagnostics: updatedDiagnostics,
          formatted_diagnosis: formattedDiagnosis,
        };
      });
      setData(_parsed);
    } else if (res.error) {
      setData([]);
    }
    setShowLoader(false);
  }

  useEffect(() => {
    _fetchAssessmentHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
  );

  return (
    <Card className="card-with-table">
      <div className="row">
        <Row className="align-items-center pb-4 px-4">
          <Col xs={"auto"} className="p-0 align-items-center">
            <h5 className="font-bold">ASSESSMENT HISTORY</h5>
          </Col>
          <Col xs={"auto"} className="px-2 pb-2">
            <Button
              type="text"
              onClick={_fetchAssessmentHistory}
              icon={<RefreshRounded />}
            />
          </Col>
        </Row>

        <AntDTable
          tableHeaders={[
            "STATUS",
            "DATE",
            "DIAGNOSIS",
            "SUBJECTIVE",
            "OBJECTIVE",
            "ASSD. BY",
            "ADDED ON",
          ]}
          headerSelector={[
            "status",
            "assessment_date",
            "formatted_diagnosis",
            "subjective",
            "objective",
            "doctor_name",
            "fomattedAddedOn",
          ]}
          tableData={data}
          loading={showLoader}
          scroll={{ x: true }}
          isClickable={false}
          withAction={true}
          ActionBtn={(row) => ActionBtn(row)}
        />
      </div>
      <ViewAssessment
        show={showViewAssessmentModal}
        onHide={() => {
          setShowViewAssessmentModal(false);
          _fetchAssessmentHistory();
        }}
        data={selectedRow}
      />
      <ViewPrescription
        show={showViewPrescriptionModal}
        onHide={() => setShowViewPrescriptionModal(false)}
        data={selectedRow}
        therapeutics={selectedRow.therapeutics || []}
        assessmentId={selectedRow.id}
        tab="assessment"
        patient={{
          full_name: selectedRow.formatted_patient_name,
          // address: selectedRow.patient_address,
          address: [
            selectedRow?.street,
            selectedRow?.barangay,
            selectedRow?.province,
            selectedRow?.city,
            selectedRow?.zip_code,
          ]
            .filter(Boolean)
            .join(", "),

          patient_sex: selectedRow.patient_sex,
          age: selectedRow.age,
          added_by: selectedRow.added_by,
          assessment_date:
            selectedRow.is_sunday_patient !== "1"
              ? formatDate(selectedRow.added_on)
              : formatDate(selectedRow.last_checkup_date),
          birthdate: selectedRow.birthdate,
        }}
        doctor={{
          doctor_id: selectedRow.doctor_id,
          doctor_name: selectedRow.doctor_name,
          ptr_no: selectedRow.ptr_no,
          license_no: selectedRow.license_no,
          sign_url: selectedRow.sign_url,
        }}
      />
      <PrintLaboratoryDiagnostics
        show={showPrintLabDiagnosticsModal}
        onHide={() => setShowPrintLabDiagnosticsModal(false)}
        diagnostics={selectedRow?.diagnostics?.[0]}
        printDiagnostics={selectedRow?.printDiagnostics}
        assessmentId={selectedRow?.id}
        patient={selectedRow}
        isNew={isNew}
      />

      <PrintXrayDiagnostics
        show={showPrintXrayDiagnosticsModal}
        onHide={() => setShowPrintXrayDiagnosticsModal(false)}
        diagnostics={selectedRow?.diagnostics?.[0]}
        printDiagnostics={selectedRow?.printDiagnostics}
        assessmentId={selectedRow?.id}
        patient={selectedRow}
        isNew={isNew}
      />

      <PrintCTScanDiagnostics
        show={showPrintCTScanDiagnosticsModal}
        onHide={() => setShowPrintCTScanDiagnosticsModal(false)}
        diagnostics={selectedRow?.diagnostics?.[0]}
        printDiagnostics={selectedRow?.printDiagnostics}
        assessmentId={selectedRow?.id}
        patient={selectedRow}
        isNew={isNew}
      />

      <PrintUltrasoundDiagnostics
        show={showPrintUltrasoundDiagnosticsModal}
        onHide={() => setShowPrintUltrasoundDiagnosticsModal(false)}
        diagnostics={selectedRow?.diagnostics?.[0]}
        printDiagnostics={selectedRow?.printDiagnostics}
        assessmentId={selectedRow?.id}
        patient={selectedRow}
        isNew={isNew}
      />

      <PrintAdmissionForm
        show={showPrintAdmissionModal}
        onHide={() => setShowPrintAdmissionModal(false)}
        assessmentData={selectedRow}
        latestVitalSigns={selectedRow?.latest_vital_sign}
        addAdmissionData={
          Array.isArray(selectedRow?.inpatients)
            ? selectedRow.inpatients[0]
            : ""
        }
        assessmentId={selectedRow?.id}
        patient={selectedRow}
      />
      {showPrintEkas && <BirForm data={selectedPatientDetails} />}
      {showPrintEpress && <PrintEpress data={selectedPatientDetails} />}
    </Card>
  );
}
export default AssessmentHistory;
